import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Globe } from 'lucide-react';
import { NavDropdown } from '@/components/nav/NavDropdown';

const LINK_CLS = 'relative flex min-h-[44px] items-center gap-0.5 whitespace-nowrap rounded-lg px-1 2xl:px-2 py-2 font-heading font-semibold text-[11px] 2xl:text-[12.5px] transition-colors';
const isMouse = (e) => e.pointerType === 'mouse';

export function DesktopLinks({ nav, isAr, pathname, openDropdown, setOpenDropdown }) {
  const pointer = useRef(null);
  const links = nav.navLinks.filter((l) => l.id !== 'home');
  const isActive = (link) => pathname === link.href.split('#')[0] || (link.dropdownId && pathname.startsWith(link.href.split('#')[0]) && link.href !== '/');

  // Mouse: hover expands and a click follows the link. Touch, pen and keyboard: click toggles the menu.
  const onClick = (link) => (e) => {
    if (!link.dropdownId) return;
    const isOpen = openDropdown === link.dropdownId;
    if (isOpen && pointer.current === 'mouse') return;
    e.preventDefault();
    setOpenDropdown(isOpen ? null : link.dropdownId);
  };
  const onFocus = (link) => (e) => { if (link.dropdownId && e.currentTarget.matches(':focus-visible')) setOpenDropdown(link.dropdownId); };
  const onBlur = (link) => (e) => {
    if (link.dropdownId && !e.currentTarget.contains(e.relatedTarget)) setOpenDropdown((cur) => (cur === link.dropdownId ? null : cur));
  };

  return (
    <div className="hidden xl:flex items-center gap-0 min-w-0">
      {links.map((link) => (
        <div
          key={link.id}
          className="relative"
          onPointerEnter={(e) => link.dropdownId && isMouse(e) && setOpenDropdown(link.dropdownId)}
          onPointerLeave={(e) => link.dropdownId && isMouse(e) && setOpenDropdown(null)}
          onPointerDown={(e) => { pointer.current = e.pointerType; }}
          onKeyDown={() => { pointer.current = 'keyboard'; }}
          onBlur={onBlur(link)}
        >
          <Link
            to={link.href}
            data-testid={`nav-link-${link.id}`}
            aria-haspopup={link.dropdownId ? 'true' : undefined}
            aria-expanded={link.dropdownId ? openDropdown === link.dropdownId : undefined}
            onFocus={onFocus(link)}
            onClick={onClick(link)}
            className={`${LINK_CLS} ${isActive(link) ? 'text-[#EE5A01]' : 'text-[#121212] hover:text-[#EE5A01] hover:bg-[#FFF4ED]'}`}
          >
            {link.shortLabel || link.label}
            {link.badge && <span data-testid={`nav-badge-${link.id}`} className="ms-1 rounded-full bg-[#EE5A01] px-1.5 py-0.5 font-heading font-black text-[9px] leading-none tracking-wider uppercase text-white">{link.badge}</span>}
            {link.dropdownId && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDropdown === link.dropdownId ? 'rotate-180' : ''}`} />}
            {isActive(link) && <span className="absolute left-2.5 right-2.5 bottom-0.5 h-0.5 rounded-full bg-[#EE5A01]" />}
          </Link>
          {link.dropdownId && openDropdown === link.dropdownId && (
            <NavDropdown dropdownId={link.dropdownId} nav={nav} isAr={isAr} alignRight={link.alignRight} onNavigate={() => setOpenDropdown(null)} />
          )}
        </div>
      ))}
    </div>
  );
}

export function NavActions({ labels, langTarget, mobileOpen, onToggleMobile }) {
  return (
    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
      <a href="tel:800364" dir="ltr" data-testid="nav-phone" className="hidden min-[2000px]:inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2.5 py-2 font-heading font-bold text-[13px] text-[#121212] hover:bg-[#FFF4ED] hover:text-[#EE5A01] transition-colors whitespace-nowrap">
        <span className="grid h-7 w-7 place-items-center rounded-md bg-[#EE5A01] text-white"><Phone className="w-3.5 h-3.5" /></span>
        {labels.phone}
      </a>
      <Link to={langTarget} data-testid="lang-toggle" aria-label="Switch language" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2.5 py-2 font-heading font-bold text-xs text-[#121212] hover:bg-[#FFF4ED] hover:text-[#EE5A01] transition-colors whitespace-nowrap">
        <Globe className="w-4 h-4" />
        <span className="xl:hidden min-[1800px]:inline">{labels.langLabel}</span>
      </Link>
      <div className="hidden sm:block">
        <Link to={labels.quoteHref} data-testid="nav-quote-btn" className="btn-primary min-h-[44px] !py-2.5 !px-4 !text-[13px] whitespace-nowrap">{labels.getQuote}</Link>
      </div>
      <button data-testid="mobile-menu-toggle" className="xl:hidden grid h-11 w-11 place-items-center rounded-lg text-[#121212] hover:bg-[#FFF4ED]" onClick={onToggleMobile} aria-label={mobileOpen ? labels.close : labels.menu} aria-expanded={mobileOpen}>
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>
    </div>
  );
}
